import { useEffect } from "react";

export const parseBackendDateTime = (value) => {
  if (!value || typeof value !== "string") return null;
  const match = value.trim().match(/^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2})(?::(\d{2}))? (AM|PM)$/i);
  if (!match) return null;

  const [, mm, dd, yyyy, hh, min, sec = "00", meridiem] = match;
  let hours = parseInt(hh, 10);
  if (meridiem.toUpperCase() === "PM" && hours !== 12) hours += 12;
  if (meridiem.toUpperCase() === "AM" && hours === 12) hours = 0;

  return new Date(parseInt(yyyy, 10), parseInt(mm, 10) - 1, parseInt(dd, 10), hours, parseInt(min, 10), parseInt(sec, 10));
};

const getOrdinalDay = (day) => {
  const mod100 = day % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${day}th`;
  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
};

export const formatNoteDateTime = (note) => {
  const candidateDate = new Date(note?.noteDate);
  const fallbackDate = parseBackendDateTime((note?.createdAtDateTime || note?.createdDate || "").trim());
  const parsedDate = !Number.isNaN(candidateDate.getTime()) ? candidateDate : fallbackDate;
  if (!parsedDate) return "";

  const isToday = parsedDate.toDateString() === new Date().toDateString();
  const timeLabel = parsedDate.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });

  if (isToday) {
    return `Today, ${timeLabel}`;
  }

  const dayLabel = getOrdinalDay(parsedDate.getDate());
  const monthLabel = parsedDate.toLocaleDateString("en-US", { month: "long" });
  return `${dayLabel} ${monthLabel}, ${timeLabel.replace(" ", "")}`;
};

export const isNoteAcknowledged = (note) => {
  return Boolean(note?.acknowledgedStatus || Number(note?.acknowledgeFlag) === 1);
};

export const useNotesTruncation = ({ notes = [], expandedNotes = {}, messageNodesRef, setTruncatedNotes, getNoteId }) => {
  useEffect(() => {
    const recalculateTruncation = () => {
      const currentIds = new Set(notes.map((note) => String(getNoteId(note))));
      const nextTruncated = {};

      Object.entries(messageNodesRef.current || {}).forEach(([noteId, node]) => {
        if (!node || !currentIds.has(noteId)) return;
        nextTruncated[noteId] = node.scrollHeight - node.clientHeight > 1;
      });

      setTruncatedNotes(nextTruncated);
    };

    const rafId = window.requestAnimationFrame(recalculateTruncation);
    window.addEventListener("resize", recalculateTruncation);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", recalculateTruncation);
    };
  }, [notes, expandedNotes, getNoteId, messageNodesRef, setTruncatedNotes]);
};
